import { mapCollectionDtoToModel } from '@hotjar/webapp/domains/collections/mapper';
import { mapLabelDtoToModel } from '@hotjar/labels';

import type {
  GenericHighlight as GenericHighlightDto,
  GenericHighlightsListItem as GenericHighlightsListItemDto,
  PaginatedResponse as PaginatedResponseDto,
} from './dto';
import type {
  ContentStatus as HighlightContentStatus,
  GenericHighlight as GenericHighlightModel,
  DeviceType,
  HeatmapType,
  GenericHighlightsListItem as GenericHighlightsListItemModel,
  PaginatedResponse as PaginatedResponseModel,
} from './model';

export function mapHighlightContentStatus(status: string): HighlightContentStatus {
  switch (status) {
    case 'creating':
      return 'creating';
    case 'done':
      return 'done';
    case 'failed':
      return 'failed';
    case 'source_deleted':
      return 'source_deleted';
    default:
      return 'unknown';
  }
}

export function mapStringToDeviceType(deviceType: string | null): DeviceType {
  switch (deviceType) {
    case 'desktop':
      return 'desktop';
    case 'phone':
      return 'phone';
    case 'tablet':
      return 'tablet';
    default:
      return 'unknown';
  }
}

export function mapStringToHeatmapType(heatmapType: string | null): HeatmapType {
  switch (heatmapType) {
    case 'click':
      return 'click';
    case 'movement':
      return 'movement';
    case 'scroll':
      return 'scroll';
    default:
      return 'unknown';
  }
}

export function mapGenericHighlightDtoToModel(dto: GenericHighlightDto): GenericHighlightModel {
  return {
    author: {
      avatar: dto.user_picture_url,
      id: String(dto.user_id),
      name: dto.user_name,
    },
    collections: dto.collections.map(mapCollectionDtoToModel),
    commentCount: dto.comment_count || 0,
    commentId: dto.comment_id ? String(dto.comment_id) : null,
    contentStatus: mapHighlightContentStatus(dto.status),
    createdDate: new Date(dto.created * 1000),
    description: dto.description,
    fullItemUrl: dto.full_item_url,
    id: String(dto.id),
    imageUrl: dto.image_url,
    labels: dto.labels.map(mapLabelDtoToModel),
    thumbnailUrl: dto.thumbnail_url,
    autoCreated: dto.auto_created,
    solved: dto.solved,
  };
}

export function mapGenericHighlightsListItemDtoToModel(
  dto: GenericHighlightsListItemDto,
): GenericHighlightsListItemModel {
  return {
    author: {
      avatar: dto.user_picture_url,
      id: String(dto.user_id),
      name: dto.user_name,
    },
    commentCount: dto.comment_count || 0,
    commentId: dto.comment_id,
    contentStatus: mapHighlightContentStatus(dto.status),
    createdDate: new Date(dto.created * 1000),
    description: dto.description,
    id: String(dto.id),
    thumbnailUrl: dto.thumbnail_url,
    seen: dto.seen,
    labels: dto.labels.map(mapLabelDtoToModel),
    autoCreated: dto.auto_created,
    solved: dto.solved,
    imageUrl: dto.image_url,
  };
}

export function mapPaginatedResponse<TItemDto, TItemModel>(
  mapItem: (dto: TItemDto) => TItemModel,
  data: PaginatedResponseDto<TItemDto>,
): PaginatedResponseModel<TItemModel> {
  return {
    firstPage: data.first_page,
    itemCount: data.item_count,
    items: data.items.map(mapItem),
    itemsPerPage: data.items_per_page,
    lastPage: data.last_page,
    nextPage: data.next_page,
    page: data.page,
    previousPage: data.previous_page,
  };
}
